/**
 * Auth store — manages authentication state and user profile.
 * Stores a server-issued session token (not the raw Google JWT).
 */
import { create } from 'zustand';
import { apiUrl } from '../config';

interface User {
  id: string;
  email: string;
  name: string;
  picture: string;
  phone: string;
  whatsapp: string;
  createdAt: string;
  lastLogin: string;
}

interface AuthState {
  user: User | null;
  token: string | null;       // Server session token (7-day TTL)
  loading: boolean;
  error: string | null;

  setAuth: (user: User, token: string) => void;
  logout: () => void;
  updateProfile: (phone: string, whatsapp: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  loading: false,
  error: null,

  setAuth: (user, token) => {
    set({ user, token, error: null });
    // Persist session token for page reload recovery
    sessionStorage.setItem('dukkan_token', token);
  },

  logout: () => {
    const { token } = get();
    // Revoke session on the server (fire-and-forget)
    if (token) {
      fetch(apiUrl('/api/auth/logout'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      }).catch(() => {});  // Best-effort, don't block UI
    }
    set({ user: null, token: null });
    sessionStorage.removeItem('dukkan_token');
  },

  updateProfile: async (phone, whatsapp) => {
    const { token } = get();
    if (!token) return;

    const res = await fetch(apiUrl('/api/auth/me'), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ phone, whatsapp }),
    });

    if (res.ok) {
      const user = await res.json();
      set({ user });
    }
  },
}));


/**
 * Helper to get auth headers for API calls.
 */
export function getAuthHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  if (!token) return {};
  return { 'Authorization': `Bearer ${token}` };
}
